import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export type SubscriptionPlan = "free" | "track" | "ai_coach" | "coach" | "partner";

export interface Subscription {
  plan: SubscriptionPlan;
  status: string;
  current_period_end: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

const PLAN_RANK: Record<SubscriptionPlan, number> = {
  free: 0,
  track: 1,
  ai_coach: 2,
  coach: 3,
  partner: 3,
};

export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;
      // Also pull latest from DB
      const { data: dbRow } = await supabase
        .from("subscriptions")
        .select("plan, status, current_period_end, stripe_customer_id, stripe_subscription_id")
        .eq("user_id", user.id)
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (dbRow) {
        setSubscription(dbRow as Subscription);
      } else {
        setSubscription({ plan: "free", status: "active", current_period_end: null, stripe_customer_id: null, stripe_subscription_id: null });
      }
    } catch {
      // Fallback: read from DB only
      const { data: dbRow } = await supabase
        .from("subscriptions")
        .select("plan, status, current_period_end, stripe_customer_id, stripe_subscription_id")
        .eq("user_id", user.id)
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (dbRow) {
        setSubscription(dbRow as Subscription);
      } else {
        setSubscription({ plan: "free", status: "active", current_period_end: null, stripe_customer_id: null, stripe_subscription_id: null });
      }
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    // Initial load from DB, then sync with Stripe
    const init = async () => {
      const { data: dbRow } = await supabase
        .from("subscriptions")
        .select("plan, status, current_period_end, stripe_customer_id, stripe_subscription_id")
        .eq("user_id", user.id)
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (dbRow) {
        setSubscription(dbRow as Subscription);
      } else {
        setSubscription({ plan: "free", status: "active", current_period_end: null, stripe_customer_id: null, stripe_subscription_id: null });
      }
      setLoading(false);

      // Then sync with Stripe in background
      refresh();
    };

    init();

    // Periodic refresh every 60s
    const interval = setInterval(refresh, 60_000);
    return () => clearInterval(interval);
  }, [user, refresh]);

  const plan = subscription?.plan ?? "free";

  const hasAccess = (requiredPlan: SubscriptionPlan): boolean => {
    return PLAN_RANK[plan] >= PLAN_RANK[requiredPlan];
  };

  const isPaid = plan !== "free";

  return {
    subscription,
    plan,
    loading,
    hasAccess,
    isPaid,
    refresh,
  };
}
